import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from "react";
import {
  Eye,
  EyeOff,
  Maximize2,
  Plus,
  RotateCcw,
  Trash2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  Background,
  BackgroundVariant,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  applyNodeChanges,
  useReactFlow,
  useViewport,
  type Node,
  type NodeChange,
  type NodeProps,
} from "@xyflow/react";
import {
  problemTreeCauseStatus,
  type ProblemTreeCause,
  type ProblemTreeCauseStatus,
  type ProblemTreeNode,
  type ProblemTreeParams,
} from "@playbookai/widget-schemas";
import {
  CauseStatusBadge,
  CauseStatusMenu,
  RULED_OUT_FADE,
} from "./CauseStatus.js";
import { widgetTargetKey } from "@playbookai/widget-schemas";
import { Button } from "../../atoms/index.js";
import { Tooltip } from "../../molecules/index.js";
import { FloatingToolbar } from "../../canvas/FloatingToolbar.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { useCameraDuration } from "../../motion.js";

type PtKind = "symptom" | "problem" | "cause";

/**
 * PB-4923 AC5 — the canonical target a sticky's text edits.
 *
 * The React Flow node id IS the effect/cause id (see `buildInitial`), so the
 * key needs nothing the node does not already carry. The trunk is handled
 * separately: it is the `problem` scalar, not a collection item, and its node
 * id is the literal string "problem".
 */
function stickyTarget(kind: PtKind, nodeId: string): string | undefined {
  const collection =
    kind === "symptom" ? "effects" : kind === "cause" ? "causes" : null;
  if (collection === null) return undefined;
  return widgetTargetKey([
    { kind: "item", collection, itemId: nodeId },
    { kind: "prop", name: "text" },
  ]);
}

interface PtNodeData extends Record<string, unknown> {
  readonly label: string;
  readonly kind: PtKind;
  readonly editable?: boolean | undefined;
  readonly autoEdit?: boolean | undefined;
  /** Causes only. Absent on a cause stored before statuses existed. */
  readonly status?: ProblemTreeCauseStatus | undefined;
  readonly onLabelChange?: ((id: string, label: string) => void) | undefined;
  readonly onDelete?: ((id: string) => void) | undefined;
  readonly onStatusChange?:
    | ((id: string, status: ProblemTreeCauseStatus) => void)
    | undefined;
}

interface TreeBackdropNodeData extends Record<string, unknown> {
  readonly pieces: TreePieces;
  readonly bounds: TreeBounds;
}

const STICKY_W = 96;
const STICKY_H = 96;
const TRUNK_W = 220;
const TRUNK_H = 64;
const SLOT_PITCH_X = 130;
const SLOT_PITCH_Y = 110;
const BUMP_W = 180;
const BUMP_H = 150;
const BUMP_RX = 70;
const STEM_W = 60;
const TRUNK_GAP_TOP = 28;
const TRUNK_GAP_BOTTOM = 24;
const MAX_EFFECTS = 16;
const MAX_CAUSES = 16;
const TRUNK_X = -TRUNK_W / 2;
const TRUNK_Y = -TRUNK_H / 2;
const TRUNK_TOP_Y = TRUNK_Y;
const TRUNK_BOTTOM_Y = TRUNK_Y + TRUNK_H;
const TRUNK_CENTER_X = 0;
const TREE_BACKDROP_ID = "__problem_tree_backdrop";
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.5;
/** Slack for the float compare, so a clamped zoom still disables its button. */
const ZOOM_EPSILON = 0.001;

export function ProblemTreeExpandedEditor({
  data,
  onChange,
  readOnly,
}: {
  readonly data: ProblemTreeParams;
  readonly onChange: (next: unknown) => void;
  readonly readOnly?: boolean | undefined;
}): ReactElement {
  return (
    <ReactFlowProvider>
      <ProblemTreeFlowEditor
        data={data}
        onChange={onChange}
        readOnly={readOnly}
      />
    </ReactFlowProvider>
  );
}

function StickyNoteNode({
  id,
  data,
  selected,
}: NodeProps<Node<PtNodeData>>): ReactElement {
  const {
    kind,
    label,
    editable,
    autoEdit,
    status,
    onLabelChange,
    onDelete,
    onStatusChange,
  } = data;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  // PB-4923 AC5. A sticky only becomes a textarea on double-click, and the
  // effect below focuses it — so the claim is registered by the ordinary focus
  // path, not by a separate "started editing" signal that could drift from it.
  const lease = useFieldEditSession(stickyTarget(kind, id));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- adopt remote labels only outside an active local draft.
    if (!editing) setDraft(label);
  }, [label, editing]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: enter edit mode once on mount for a freshly-created auto-edit node.
    if (editable && autoEdit) setEditing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!editing) return;
    const target = textAreaRef.current;
    target?.focus();
    target?.select();
  }, [editing]);

  const tone =
    kind === "symptom"
      ? "bg-pb-illustration-symptom text-pb-illustration-symptom-text"
      : "bg-pb-illustration-cause text-pb-illustration-cause-text";
  const ring = selected ? "shadow-glow-selected" : "";
  const causeStatus = kind === "cause" ? (status ?? "suspected") : undefined;
  const faded = causeStatus === "ruled_out" ? RULED_OUT_FADE : "";

  const commit = (): void => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== label) onLabelChange?.(id, next);
    else setDraft(label);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- mouse-only double-click-to-edit affordance; keyboard editing is deferred to a future canvas-wide keyboard/focus model (PB-153 residual inventory).
    <div
      className={`group relative ${tone} ${ring} ${faded} shadow-md`}
      style={{
        width: STICKY_W,
        height: STICKY_H,
        transform: `rotate(${stickyRotation(id)}deg)`,
      }}
      title={editing ? undefined : label}
      onDoubleClick={(event) => {
        if (!editable) return;
        event.stopPropagation();
        setEditing(true);
      }}
    >
      {editable && onDelete && !editing ? (
        <Tooltip
          content="Delete sticky"
          describeChild={false}
          wrapperClassName="absolute top-0.5 end-0.5 z-10"
        >
          <Button
            variant="plain"
            size="xs"
            iconOnly
            onClick={(event) => {
              event.stopPropagation();
              onDelete(id);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            aria-label="Delete sticky"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </Tooltip>
      ) : null}

      {editable && editing ? (
        <Tooltip
          content="Up to 80 characters per sticky"
          wrapperClassName="absolute inset-0"
        >
          <textarea
            ref={textAreaRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onFocus={lease.onFocus}
            onBlur={() => {
              lease.onBlur();
              commit();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.blur();
              } else if (event.key === "Escape") {
                event.preventDefault();
                setDraft(label);
                setEditing(false);
              }
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            className="nodrag nopan absolute inset-0 rounded-md bg-pb-card/40 border-0 focus-field resize-none px-1.5 py-2 text-dense-2 leading-tight text-center font-medium"
            maxLength={80}
            aria-label={`${kind} text`}
          />
        </Tooltip>
      ) : (
        <div
          className={`absolute inset-0 flex items-center justify-center px-1.5 pt-2 text-dense-2 leading-tight text-center font-medium overflow-hidden select-none ${
            causeStatus ? "pb-6" : "pb-2"
          }`}
        >
          {label}
        </div>
      )}

      {/* The status sits OUTSIDE the textarea branch: editing a cause's label
          must not take its status away, and a read-only canvas still reads it. */}
      {causeStatus ? (
        <div className="absolute inset-x-1 bottom-1 flex justify-center">
          {editable && onStatusChange ? (
            <CauseStatusMenu
              status={causeStatus}
              causeLabel={label}
              onChange={(next) => onStatusChange(id, next)}
            />
          ) : (
            <CauseStatusBadge status={causeStatus} />
          )}
        </div>
      ) : null}
    </div>
  );
}

function ProblemTrunkNode({
  id,
  data,
  selected,
}: NodeProps<Node<PtNodeData>>): ReactElement {
  const { label, editable, onLabelChange } = data;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  // PB-4923 AC5. The trunk is the `problem` scalar, not a collection item.
  const lease = useFieldEditSession("problem");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- adopt remote labels only outside an active local draft.
    if (!editing) setDraft(label);
  }, [label, editing]);
  useEffect(() => {
    if (!editing) return;
    const target = textAreaRef.current;
    target?.focus();
    target?.select();
  }, [editing]);

  const ring = selected ? "shadow-glow-selected" : "";

  const commit = (): void => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== label) onLabelChange?.(id, next);
    else setDraft(label);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- mouse-only double-click-to-edit affordance; keyboard editing is deferred to a future canvas-wide keyboard/focus model (PB-153 residual inventory).
    <div
      className={`relative bg-pb-illustration-problem text-pb-illustration-problem-text border border-pb-illustration-problem-border/70 shadow-md ${ring}`}
      style={{
        width: TRUNK_W,
        height: TRUNK_H,
      }}
      title={editing ? undefined : label}
      onDoubleClick={(event) => {
        if (!editable) return;
        event.stopPropagation();
        setEditing(true);
      }}
    >
      {editable && editing ? (
        <textarea
          ref={textAreaRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={lease.onFocus}
          onBlur={() => {
            lease.onBlur();
            commit();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.blur();
            } else if (event.key === "Escape") {
              event.preventDefault();
              setDraft(label);
              setEditing(false);
            }
          }}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          className="nodrag nopan absolute inset-0 rounded-md bg-pb-card/40 border-0 focus-field resize-none px-3 py-2 text-title leading-tight text-center font-bold"
          aria-label="Problem text"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center px-3 py-2 text-title leading-tight text-center font-bold overflow-hidden select-none">
          {label || "Problem"}
        </div>
      )}
    </div>
  );
}

/* Kept hand-built (design-system adoption): the sticky, trunk and backdrop
   nodes are React Flow nodes on the tree canvas — sticky paper and tree
   illustration placed in flow space — so no design-system part draws them. */
const NODE_TYPES = {
  effectSticky: StickyNoteNode,
  causeSticky: StickyNoteNode,
  problemTrunk: ProblemTrunkNode,
  treeBackdrop: TreeBackdropNode,
};

function nodeTypeFor(
  kind: PtKind,
): "effectSticky" | "causeSticky" | "problemTrunk" {
  if (kind === "symptom") return "effectSticky";
  if (kind === "cause") return "causeSticky";
  return "problemTrunk";
}

function TreeBackdropNode({
  data,
}: NodeProps<Node<TreeBackdropNodeData>>): ReactElement {
  return (
    <div
      aria-hidden
      className="pointer-events-none"
      style={{
        position: "relative",
        width: data.bounds.width,
        height: data.bounds.height,
      }}
    >
      <TreeIllustration pieces={data.pieces} bounds={data.bounds} />
    </div>
  );
}

function ProblemTreeFlowEditor({
  data,
  onChange,
  readOnly,
}: {
  readonly data: ProblemTreeParams;
  readonly onChange: (next: unknown) => void;
  readonly readOnly?: boolean | undefined;
}): ReactElement {
  const idCounterRef = useRef(0);
  const generateId = useCallback((kind: PtKind): string => {
    idCounterRef.current += 1;
    return `${kind[0]}-${Date.now().toString(36)}-${idCounterRef.current}`;
  }, []);

  const renameNodeRef = useRef<(id: string, label: string) => void>(() => {});
  const deleteNodeRef = useRef<(id: string) => void>(() => {});
  const setNodeStatusRef = useRef<
    (id: string, status: ProblemTreeCauseStatus) => void
  >(() => {});

  const nodeData = useCallback(
    (
      label: string,
      kind: PtKind,
      autoEdit = false,
      status?: ProblemTreeCauseStatus | undefined,
    ): PtNodeData => ({
      label,
      kind,
      editable: !readOnly,
      autoEdit,
      ...(kind === "cause" ? { status: status ?? "suspected" } : {}),
      onLabelChange: (id, nextLabel) => renameNodeRef.current(id, nextLabel),
      onDelete: (id) => deleteNodeRef.current(id),
      onStatusChange: (id, next) => setNodeStatusRef.current(id, next),
    }),
    [readOnly],
  );

  const buildInitial = useCallback((): Node<PtNodeData>[] => {
    const effects = visibleNodes(data.effects, "Symptom");
    const causes: ProblemTreeCause[] = visibleNodes(data.causes, "Cause");
    const problem = data.problem.trim() || "Problem statement";
    const result: Node<PtNodeData>[] = [];

    effects.forEach((effect, index) => {
      const slot = effectSlot(index, effects.length);
      result.push({
        id: effect.id,
        type: nodeTypeFor("symptom"),
        position: { x: slot.x, y: slot.y },
        data: nodeData(effect.text, "symptom"),
        width: STICKY_W,
        height: STICKY_H,
        zIndex: 2,
        style: { width: STICKY_W, height: STICKY_H },
      });
    });

    result.push({
      id: "problem",
      type: nodeTypeFor("problem"),
      position: { x: TRUNK_X, y: TRUNK_Y },
      data: nodeData(problem, "problem"),
      width: TRUNK_W,
      height: TRUNK_H,
      zIndex: 2,
      style: { width: TRUNK_W, height: TRUNK_H },
      deletable: false,
    });

    causes.forEach((cause, index) => {
      const slot = causeSlot(index, causes.length);
      result.push({
        id: cause.id,
        type: nodeTypeFor("cause"),
        position: { x: slot.x, y: slot.y },
        data: nodeData(
          cause.text,
          "cause",
          false,
          problemTreeCauseStatus(cause),
        ),
        width: STICKY_W,
        height: STICKY_H,
        zIndex: 2,
        style: { width: STICKY_W, height: STICKY_H },
      });
    });

    return result;
  }, [data.causes, data.effects, data.problem, nodeData]);

  // eslint-disable-next-line react-hooks/refs -- buildInitial is a lazy initializer whose node-data callbacks intentionally defer to renameNodeRef/deleteNodeRef (assigned in the effects below) to avoid stale closures; no ref value is read during render.
  const [nodes, setNodes] = useState<Node<PtNodeData>[]>(buildInitial);
  const onChangeRef = useRef(onChange);
  const lastEmittedParamsRef = useRef(JSON.stringify(toParams(nodes)));
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useLayoutEffect(() => {
    if (readOnly) return;
    const params = toParams(nodes);
    const serialized = JSON.stringify(params);
    if (serialized === lastEmittedParamsRef.current) return;
    lastEmittedParamsRef.current = serialized;
    // The host owns persistence and debouncing. Publish before the editor can
    // close; cancelling a local timer on unmount used to discard the last edit.
    onChangeRef.current(params);
  }, [nodes, readOnly]);

  /* PB-4923 — adopt a colleague's change while this editor is open.
     The node graph is built ONCE from `data`, so a cause somebody else added
     (or renamed) never appeared here, and the next save rebuilt params from
     this stale graph: their cause was described as REMOVED and their rename as
     changed back. The host cannot see that — the omission really is in what
     this editor emitted.

     Node ids and types stay stable, so React keeps each leaf editor mounted.
     Each leaf retains its focused draft while adopting other remote fields;
     its eventual commit updates the latest graph. Positions are derived. */
  const serverSignature = paramsSignature(data);
  useEffect(() => {
    // Read-only adopts too: nothing local to protect, and a viewer should not
    // be looking at the tree as it was when they opened it.
    setNodes((current) => {
      // Compare what each graph would SAVE, not the props against the graph:
      // `toParams` normalises an empty problem to its placeholder and drops
      // hidden nodes, so comparing raw props would report a difference on
      // every mount and rebuild the tree for nothing.
      const next = buildInitial();
      if (
        paramsSignature(toParams(current)) === paramsSignature(toParams(next))
      ) {
        return current;
      }
      // The rebuild is remote: do not emit it back as a local edit.
      lastEmittedParamsRef.current = JSON.stringify(toParams(next));
      return next;
    });
    /* Keyed on the SIGNATURE: `data` is a fresh object on every host render,
       and `buildInitial` closes over it, so either one as a dependency re-runs
       this constantly — which would rebuild the tree mid-edit and discard the
       local draft this effect exists to preserve.

       The directive must sit on the line IMMEDIATELY above the dependency
       array. It was previously the first line of a multi-line `//` comment, so
       it disabled the comment line beneath it and did nothing: exhaustive-deps
       reports at the array, which was three lines further down and still
       warning. */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [serverSignature]);

  const renameNode = useCallback((id: string, label: string): void => {
    setNodes((current) =>
      current.map((node) =>
        node.id === id
          ? { ...node, data: { ...node.data, label, autoEdit: false } }
          : node,
      ),
    );
  }, []);
  useEffect(() => {
    renameNodeRef.current = renameNode;
  }, [renameNode]);

  const setNodeStatus = useCallback(
    (id: string, status: ProblemTreeCauseStatus): void => {
      setNodes((current) =>
        current.map((node) =>
          node.id === id && node.data.kind === "cause"
            ? { ...node, data: { ...node.data, status } }
            : node,
        ),
      );
    },
    [],
  );
  useEffect(() => {
    setNodeStatusRef.current = setNodeStatus;
  }, [setNodeStatus]);

  const deleteNode = useCallback((id: string): void => {
    setNodes((current) => {
      const target = current.find((node) => node.id === id);
      if (!target || target.data.kind === "problem") return current;
      return current.filter((node) => node.id !== id);
    });
  }, []);
  useEffect(() => {
    deleteNodeRef.current = deleteNode;
  }, [deleteNode]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]): void => {
      if (readOnly) return;
      setNodes((current) => {
        const currentIds = new Set(current.map((node) => node.id));
        const filtered = changes.filter((change) => {
          if (change.type === "add") return false;
          if (!currentIds.has(change.id)) return false;
          if (change.type !== "remove") return true;
          const target = current.find((node) => node.id === change.id);
          return target?.data.kind !== "problem";
        });
        return applyNodeChanges(filtered, current) as Node<PtNodeData>[];
      });
    },
    [readOnly],
  );

  const effectCount = useMemo(
    () => nodes.filter((node) => node.data.kind === "symptom").length,
    [nodes],
  );
  const causeCount = useMemo(
    () => nodes.filter((node) => node.data.kind === "cause").length,
    [nodes],
  );
  // Ruled-out causes are SHOWN by default, faded. Hiding is a view preference
  // on top of `nodes` — the node itself stays in state, so `toParams` keeps
  // writing it and nothing is lost by toggling.
  const [hideRuledOut, setHideRuledOut] = useState(false);
  const ruledOutCount = useMemo(
    () =>
      nodes.filter(
        (node) =>
          node.data.kind === "cause" && node.data.status === "ruled_out",
      ).length,
    [nodes],
  );
  const displayNodes = useMemo(
    () =>
      hideRuledOut
        ? nodes.map((node) =>
            node.data.kind === "cause" && node.data.status === "ruled_out"
              ? { ...node, hidden: true, selected: false }
              : node,
          )
        : nodes,
    [hideRuledOut, nodes],
  );
  const renderedNodes = useMemo(
    () => [createTreeBackdropNode(displayNodes), ...displayNodes],
    [displayNodes],
  );

  const addEffect = useCallback((): void => {
    if (readOnly) return;
    setNodes((current) => {
      const total = current.filter(
        (node) => node.data.kind === "symptom",
      ).length;
      if (total >= MAX_EFFECTS) return current;
      const id = generateId("symptom");
      const slot = effectSlot(total, total + 1);
      const newNode: Node<PtNodeData> = {
        id,
        type: nodeTypeFor("symptom"),
        position: { x: slot.x, y: slot.y },
        data: nodeData("New effect", "symptom", true),
        width: STICKY_W,
        height: STICKY_H,
        zIndex: 2,
        style: { width: STICKY_W, height: STICKY_H },
        selected: true,
      };
      return [
        ...current.map((node) => ({ ...node, selected: false })),
        newNode,
      ];
    });
  }, [generateId, nodeData, readOnly]);

  const addCause = useCallback((): void => {
    if (readOnly) return;
    setNodes((current) => {
      const total = current.filter((node) => node.data.kind === "cause").length;
      if (total >= MAX_CAUSES) return current;
      const id = generateId("cause");
      const slot = causeSlot(total, total + 1);
      const newNode: Node<PtNodeData> = {
        id,
        type: nodeTypeFor("cause"),
        position: { x: slot.x, y: slot.y },
        data: nodeData("New root cause", "cause", true),
        width: STICKY_W,
        height: STICKY_H,
        zIndex: 2,
        style: { width: STICKY_W, height: STICKY_H },
        selected: true,
      };
      return [
        ...current.map((node) => ({ ...node, selected: false })),
        newNode,
      ];
    });
  }, [generateId, nodeData, readOnly]);

  const { fitView } = useReactFlow();
  /* ReactFlow flies the viewport in JavaScript, so the `prefers-reduced-motion`
     block in `theme.css` never reaches it — this hook is the only thing that
     carries the setting across that boundary, and it returns 0 when the reader
     has asked for reduced motion (ADR-0091). Both call sites below re-frame the
     whole tree, hence `framing`. */
  const framingDuration = useCameraDuration("framing");

  const autoArrange = useCallback((): void => {
    setNodes((current) => {
      const effects = current.filter((node) => node.data.kind === "symptom");
      const causes = current.filter((node) => node.data.kind === "cause");
      const effectIndex = new Map(
        effects.map((node, index) => [node.id, index]),
      );
      const causeIndex = new Map(causes.map((node, index) => [node.id, index]));
      return current.map((node) => {
        if (node.data.kind === "symptom") {
          const slot = effectSlot(
            effectIndex.get(node.id) ?? 0,
            effects.length,
          );
          return { ...node, position: { x: slot.x, y: slot.y } };
        }
        if (node.data.kind === "cause") {
          const slot = causeSlot(causeIndex.get(node.id) ?? 0, causes.length);
          return { ...node, position: { x: slot.x, y: slot.y } };
        }
        return { ...node, position: { x: TRUNK_X, y: TRUNK_Y } };
      });
    });
    window.setTimeout(
      () => fitView({ padding: 0.05, duration: framingDuration }),
      30,
    );
  }, [fitView, framingDuration]);

  const resetZoom = useCallback((): void => {
    fitView({ padding: 0.05, duration: framingDuration });
  }, [fitView, framingDuration]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>): void => {
      if (readOnly) return;
      const tag = (event.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (event.key !== "Backspace" && event.key !== "Delete") return;
      setNodes((current) => {
        const selected = current.filter(
          (node) => node.selected && node.data.kind !== "problem",
        );
        if (selected.length === 0) return current;
        event.preventDefault();
        const ids = new Set(selected.map((node) => node.id));
        return current.filter((node) => !ids.has(node.id));
      });
    },
    [readOnly],
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Keyboard-pannable canvas surface: focusable wrapper is the APG
          "application" pattern for ReactFlow; the rule's allowlist has no
          fitting role, hence the documented disable (block form — survives
          prettier re-wrapping the attributes). */}
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="application"
        aria-label="Problem tree canvas"
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="flex-1 min-h-0 rounded-md border border-pb-border bg-pb-card-elevated/40 overflow-hidden relative focus-ring"
      >
        {/* eslint-enable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */}
        <ReactFlow
          nodes={renderedNodes}
          edges={[]}
          nodeTypes={NODE_TYPES}
          onNodesChange={onNodesChange}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={!readOnly}
          nodesConnectable={false}
          elementsSelectable={!readOnly}
          panOnDrag
          panOnScroll={false}
          selectionOnDrag={false}
          zoomOnScroll
          zoomOnPinch
          zoomOnDoubleClick={false}
          preventScrolling
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          fitView
          fitViewOptions={{ padding: 0.05 }}
          deleteKeyCode={null}
          style={{ background: "transparent" }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={16}
            size={1}
            color="hsl(var(--pb-border) / 0.4)"
          />
          <Panel position="bottom-right" className="!m-3">
            <ZoomControls onFitView={resetZoom} />
          </Panel>

          <Panel position="top-left" className="!m-3">
            <FloatingToolbar
              label="Add stickies"
              surface="glass"
              shape="rounded"
            >
              <Button
                variant="soft"
                tone="healthy"
                size="sm"
                onClick={addEffect}
                disabled={readOnly || effectCount >= MAX_EFFECTS}
              >
                <Plus className="w-3.5 h-3.5" /> Add Effect
              </Button>
              <Button
                variant="soft"
                tone="watch"
                size="sm"
                onClick={addCause}
                disabled={readOnly || causeCount >= MAX_CAUSES}
              >
                <Plus className="w-3.5 h-3.5" /> Add Root Cause
              </Button>
            </FloatingToolbar>
          </Panel>

          <Panel position="top-right" className="!m-3">
            <FloatingToolbar label="Tree view" surface="glass" shape="rounded">
              <Tooltip content="Snap stickies back to a tidy tree shape">
                <Button size="sm" onClick={autoArrange}>
                  <RotateCcw className="w-3.5 h-3.5" /> Auto-arrange
                </Button>
              </Tooltip>
              {ruledOutCount > 0 ? (
                <Tooltip content="Ruled-out causes stay saved either way">
                  <Button
                    size="sm"
                    onClick={() => setHideRuledOut((current) => !current)}
                    aria-pressed={hideRuledOut}
                  >
                    {hideRuledOut ? (
                      <Eye className="w-3.5 h-3.5" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5" />
                    )}
                    {hideRuledOut ? "Show" : "Hide"} ruled out ({ruledOutCount})
                  </Button>
                </Tooltip>
              ) : null}
              <Tooltip content="Reset zoom and re-fit the tree to view">
                <Button size="sm" onClick={resetZoom}>
                  <Maximize2 className="w-3.5 h-3.5" /> Reset zoom
                </Button>
              </Tooltip>
            </FloatingToolbar>
          </Panel>

          <Panel position="bottom-left" className="!m-3">
            <div className="rounded-md border glass px-2.5 py-1.5 shadow-sm text-dense-2 leading-tight flex items-center gap-3">
              <LegendDot color="bg-pb-illustration-symptom" label="Effects" />
              <LegendDot color="bg-pb-illustration-problem" label="Problem" />
              <LegendDot color="bg-pb-illustration-cause" label="Root causes" />
            </div>
          </Panel>
        </ReactFlow>

        {effectCount === 0 && causeCount === 0 ? (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-pb-text-muted text-caption italic text-center max-w-xs">
              Empty tree - add effects, root causes, or rename the Problem to
              get started.
            </div>
          </div>
        ) : null}
      </div>

      <div className="shrink-0 flex items-center gap-1.5 text-dense-1 text-pb-text-muted">
        <span className="inline-block w-1 h-1 rounded-full bg-pb-text-muted/60" />
        Drag stickies to reshape the tree - Double-click text to edit - Scroll /
        pinch to zoom - Backspace to delete a selected sticky - Sticky text: up
        to 80 characters
      </div>
    </div>
  );
}

/**
 * The zoom control group, themed from design tokens rather than xyflow's CSS.
 *
 * `@xyflow/react/dist/style.css` paints `.react-flow__controls-button` with a
 * literal near-white background and lets its icon inherit `currentColor`, and
 * the shell imports that stylesheet OUTSIDE Tailwind's cascade layers — where an
 * unlayered rule beats every layered utility whatever its specificity. On the
 * dark theme the stock `<Controls>` therefore rendered as a white block with
 * white icons inside it, which is the reported defect. Only styling the
 * CONTAINER (what the previous `className` did) cannot reach the buttons.
 *
 * Rebuilding the group out of the same tokens the sibling panels in this file
 * already use makes it follow every theme, including high contrast, with no
 * dependence on xyflow's internal class names or variable spellings. Behaviour
 * is unchanged: the same `zoomIn`/`zoomOut`/`fitView` from the same flow
 * instance, real `<button>`s for keyboard use, and each end of the zoom range
 * disabling its own button exactly as the stock controls did.
 */
function ZoomControls({
  onFitView,
}: {
  readonly onFitView: () => void;
}): ReactElement {
  const { zoomIn, zoomOut } = useReactFlow();
  // Subscribed HERE rather than in the editor so a pan or a pinch re-renders
  // three buttons instead of the whole canvas.
  const { zoom } = useViewport();
  // A discrete zoom in or out, and 0 under reduced motion (ADR-0091).
  const zoomStepDuration = useCameraDuration("step");
  return (
    <FloatingToolbar
      label="Zoom"
      orientation="vertical"
      surface="glass"
      shape="rounded"
    >
      <Tooltip content="Zoom in" describeChild={false}>
        <Button
          size="sm"
          iconOnly
          onClick={() => zoomIn({ duration: zoomStepDuration })}
          disabled={zoom >= MAX_ZOOM - ZOOM_EPSILON}
          aria-label="Zoom in"
        >
          <ZoomIn className="w-3.5 h-3.5" aria-hidden="true" />
        </Button>
      </Tooltip>
      <Tooltip content="Zoom out" describeChild={false}>
        <Button
          size="sm"
          iconOnly
          onClick={() => zoomOut({ duration: zoomStepDuration })}
          disabled={zoom <= MIN_ZOOM + ZOOM_EPSILON}
          aria-label="Zoom out"
        >
          <ZoomOut className="w-3.5 h-3.5" aria-hidden="true" />
        </Button>
      </Tooltip>
      <Tooltip content="Fit the tree to the view">
        <Button size="sm" iconOnly onClick={onFitView} aria-label="Fit view">
          <Maximize2 className="w-3.5 h-3.5" aria-hidden="true" />
        </Button>
      </Tooltip>
    </FloatingToolbar>
  );
}

function LegendDot({
  color,
  label,
}: {
  readonly color: string;
  readonly label: string;
}): ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${color}`} />
      <span className="font-medium text-pb-text">{label}</span>
    </span>
  );
}

interface SlotInfo {
  readonly x: number;
  readonly y: number;
}

interface CenterPoint {
  readonly id: string;
  readonly cx: number;
  readonly cy: number;
}

interface TreePieces {
  readonly effects: ReadonlyArray<CenterPoint>;
  readonly causes: ReadonlyArray<CenterPoint>;
  readonly trunkCenter: {
    readonly cx: number;
    readonly topY: number;
    readonly bottomY: number;
  };
}

interface TreeBounds {
  readonly minX: number;
  readonly minY: number;
  readonly width: number;
  readonly height: number;
}

function TreeIllustration({
  pieces,
  bounds,
}: {
  readonly pieces: TreePieces;
  readonly bounds: TreeBounds;
}): ReactElement {
  const { effects, causes, trunkCenter } = pieces;
  const stemX = trunkCenter.cx - STEM_W / 2;
  const stemTop =
    effects.length > 0
      ? Math.max(...effects.map((point) => point.cy + BUMP_H / 2)) - 30
      : trunkCenter.topY - 8;
  const stemBottom = trunkCenter.topY + 8;
  const stemH = Math.max(0, stemBottom - stemTop);

  return (
    <svg
      width={bounds.width}
      height={bounds.height}
      viewBox={`${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}`}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      <defs>
        <filter
          id="pt-goo-expanded"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" />
          <feColorMatrix
            mode="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -10"
          />
        </filter>
      </defs>

      {causes.map((point) => {
        const sx = trunkCenter.cx;
        const sy = trunkCenter.bottomY;
        const ex = point.cx;
        const ey = point.cy;
        const dx = ex - sx;
        const dy = Math.max(40, ey - sy);
        const jitter = stickyJitter(point.id, 7) * 40;
        const c1x = sx + dx * 0.15 + jitter;
        const c1y = sy + dy * 0.45;
        const c2x = ex - dx * 0.1 + jitter * 0.5;
        const c2y = ey - dy * 0.25;
        return (
          <g key={`root-${point.id}`}>
            <path
              d={`M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${ex} ${ey + 10}`}
              fill="none"
              strokeWidth={5.5}
              strokeLinecap="round"
              className="stroke-pb-illustration-root"
            />
            <ellipse
              cx={ex}
              cy={ey + STICKY_H / 2 + 4}
              rx={36}
              ry={14}
              className="fill-pb-illustration-root/15"
            />
          </g>
        );
      })}

      <rect
        x={trunkCenter.cx - 40}
        y={trunkCenter.topY - 100}
        width={80}
        height={TRUNK_H + 200}
        rx={10}
        ry={10}
        className="fill-pb-illustration-trunk"
      />

      {effects.length > 0 || stemH > 0 ? (
        <g
          filter="url(#pt-goo-expanded)"
          className="fill-pb-illustration-foliage"
        >
          {effects.map((point) => (
            <rect
              key={`foliage-${point.id}`}
              x={point.cx - BUMP_W / 2}
              y={point.cy - BUMP_H / 2}
              width={BUMP_W}
              height={BUMP_H}
              rx={BUMP_RX}
              ry={BUMP_RX}
            />
          ))}
          {stemH > 0 ? (
            <rect
              x={stemX - 2}
              y={stemTop}
              width={STEM_W + 4}
              height={stemH}
              rx={20}
              ry={20}
            />
          ) : null}
        </g>
      ) : null}
    </svg>
  );
}

function createTreeBackdropNode(
  nodes: ReadonlyArray<Node<PtNodeData>>,
): Node<TreeBackdropNodeData> {
  const pieces = treePiecesFromNodes(nodes);
  const bounds = treeBoundsFromPieces(pieces);
  return {
    id: TREE_BACKDROP_ID,
    type: "treeBackdrop",
    position: { x: bounds.minX, y: bounds.minY },
    data: { pieces, bounds },
    width: bounds.width,
    height: bounds.height,
    draggable: false,
    selectable: false,
    connectable: false,
    deletable: false,
    focusable: false,
    zIndex: 0,
    style: {
      width: bounds.width,
      height: bounds.height,
      pointerEvents: "none",
      zIndex: 0,
    },
  };
}

function treePiecesFromNodes(
  nodes: ReadonlyArray<Node<PtNodeData>>,
): TreePieces {
  return {
    effects: nodes
      .filter((node) => node.data.kind === "symptom" && node.hidden !== true)
      .map(effectCenter),
    causes: nodes
      .filter((node) => node.data.kind === "cause" && node.hidden !== true)
      .map(causeCenter),
    trunkCenter: trunkPiece(nodes.find((node) => node.data.kind === "problem")),
  };
}

function treeBoundsFromPieces(pieces: TreePieces): TreeBounds {
  const { effects, causes, trunkCenter } = pieces;
  const allX: number[] = [
    trunkCenter.cx - TRUNK_W / 2,
    trunkCenter.cx + TRUNK_W / 2,
  ];
  const allY: number[] = [trunkCenter.topY, trunkCenter.bottomY];

  effects.forEach((point) => {
    allX.push(point.cx - BUMP_W / 2, point.cx + BUMP_W / 2);
    allY.push(point.cy - BUMP_H / 2, point.cy + BUMP_H / 2);
  });
  causes.forEach((point) => {
    allX.push(point.cx - 60, point.cx + 60);
    allY.push(point.cy - 30, point.cy + 60);
  });

  const pad = 30;
  const minX = Math.min(...allX) - pad;
  const maxX = Math.max(...allX) + pad;
  const minY = Math.min(...allY) - pad;
  const maxY = Math.max(...allY) + pad;
  return {
    minX,
    minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

function effectCenter(node: Node<PtNodeData>): CenterPoint {
  return {
    id: node.id,
    cx: node.position.x + STICKY_W / 2,
    cy: node.position.y + STICKY_H / 2,
  };
}

function causeCenter(node: Node<PtNodeData>): CenterPoint {
  return {
    id: node.id,
    cx: node.position.x + STICKY_W / 2,
    cy: node.position.y + STICKY_H / 2,
  };
}

function trunkPiece(
  node: Node<PtNodeData> | undefined,
): TreePieces["trunkCenter"] {
  if (!node)
    return { cx: TRUNK_CENTER_X, topY: TRUNK_TOP_Y, bottomY: TRUNK_BOTTOM_Y };
  return {
    cx: node.position.x + TRUNK_W / 2,
    topY: node.position.y,
    bottomY: node.position.y + TRUNK_H,
  };
}

function visibleNodes(
  nodes: ReadonlyArray<ProblemTreeNode>,
  label: "Symptom" | "Cause",
): ProblemTreeNode[] {
  if (nodes.length > 0) return [...nodes];
  return Array.from({ length: 3 }, (_, index) => ({
    id: `${label.toLowerCase()}-${index + 1}`,
    text: `${label} ${index + 1}`,
  }));
}

/**
 * Content-only, order-stable identity for a set of params.
 *
 * `JSON.stringify` of the objects themselves would compare KEY ORDER too, and
 * `toParams` builds its result in a different order from the props — so the two
 * would never match and the adoption effect would rebuild the tree on every
 * render.
 */
function paramsSignature(params: ProblemTreeParams): string {
  return JSON.stringify([
    params.problem,
    params.effects.map((effect) => [effect.id, effect.text]),
    params.causes.map((cause) => [
      cause.id,
      cause.text,
      cause.status ?? "suspected",
    ]),
  ]);
}

function toParams(nodes: ReadonlyArray<Node<PtNodeData>>): ProblemTreeParams {
  const effects = nodes
    .filter((node) => node.data.kind === "symptom")
    .map((node) => ({ id: node.id, text: node.data.label }));
  // Hidden ruled-out causes are still nodes, so hiding one never removes it
  // from the saved params.
  const causes = nodes
    .filter((node) => node.data.kind === "cause")
    .map((node) => ({
      id: node.id,
      text: node.data.label,
      status: node.data.status ?? "suspected",
    }));
  const problem =
    nodes.find((node) => node.data.kind === "problem")?.data.label.trim() ||
    "Problem statement";
  return { effects, problem, causes };
}

function distributeRows(count: number, maxPerRow = 4): number[] {
  if (count <= 0) return [];
  const rows = Math.min(Math.ceil(count / maxPerRow), 4);
  const base = Math.floor(count / rows);
  const extras = count - base * rows;
  return Array.from(
    { length: rows },
    (_, index) => base + (index >= rows - extras ? 1 : 0),
  );
}

const EFFECT_ROW_TABLE: ReadonlyArray<readonly number[]> = [
  [],
  [1],
  [2],
  [1, 2],
  [1, 3],
  [2, 3],
  [1, 2, 3],
  [2, 2, 3],
  [2, 3, 3],
  [1, 3, 5],
  [1, 2, 3, 4],
  [1, 3, 3, 4],
  [2, 2, 4, 4],
  [1, 3, 4, 5],
  [2, 3, 4, 5],
  [3, 3, 4, 5],
  [1, 3, 5, 7],
];

function distributeEffectRows(count: number): number[] {
  if (count <= 0) return [];
  const preset = EFFECT_ROW_TABLE[count];
  if (preset) return [...preset];
  const rowCount = 4;
  const base = Math.floor(count / rowCount);
  const extras = count - base * rowCount;
  return Array.from(
    { length: rowCount },
    (_, index) => base + (index >= rowCount - extras ? 1 : 0),
  );
}

function effectSlot(index: number, total: number): SlotInfo {
  const rows = distributeEffectRows(total);
  let acc = 0;
  let rowIdx = 0;
  let inRow = 0;
  for (let row = 0; row < rows.length; row += 1) {
    if (index < acc + rows[row]!) {
      rowIdx = row;
      inRow = index - acc;
      break;
    }
    acc += rows[row]!;
  }
  const perRow = rows[rowIdx] ?? 1;
  const rowWidth = perRow * SLOT_PITCH_X;
  const startX = TRUNK_CENTER_X - rowWidth / 2 + (SLOT_PITCH_X - STICKY_W) / 2;
  const distanceFromTrunk = (rows.length - rowIdx) * SLOT_PITCH_Y;
  const slotTopY = TRUNK_TOP_Y - TRUNK_GAP_TOP - distanceFromTrunk;
  return {
    x: startX + inRow * SLOT_PITCH_X,
    y: slotTopY + (SLOT_PITCH_Y - STICKY_H) / 2,
  };
}

function causeSlot(index: number, total: number): SlotInfo {
  const rows = distributeRows(total);
  let acc = 0;
  let rowIdx = 0;
  let inRow = 0;
  for (let row = 0; row < rows.length; row += 1) {
    if (index < acc + rows[row]!) {
      rowIdx = row;
      inRow = index - acc;
      break;
    }
    acc += rows[row]!;
  }
  const perRow = rows[rowIdx] ?? 1;
  const rowWidth = perRow * SLOT_PITCH_X;
  const startX = TRUNK_CENTER_X - rowWidth / 2 + (SLOT_PITCH_X - STICKY_W) / 2;
  const slotTopY = TRUNK_BOTTOM_Y + TRUNK_GAP_BOTTOM + rowIdx * SLOT_PITCH_Y;
  return {
    x: startX + inRow * SLOT_PITCH_X,
    y: slotTopY + (SLOT_PITCH_Y - STICKY_H) / 2,
  };
}

function stickyRotation(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 7) - 3;
}

function stickyJitter(id: string, salt: number): number {
  let hash = salt;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 33 + id.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 100) / 50 - 1;
}
